// The day: dawn announcement, then the town square where players nominate, shoot, make
// statements, choose on dying, and ready up.
import { useState } from "react";
import { Countdown } from "../components/Countdown";
import { PhaseHeader } from "../components/PhaseHeader";
import { Sheet } from "../components/Sheet";
import { StatementSheet } from "../components/StatementSheet";
import { TownSquare } from "../components/TownSquare";
import { useCommand } from "../hooks/useCommand";
import type { PlayerView, SeatPublic } from "../protocol";
import { dawnHeadline, revivedHeadline, seatName } from "../state/selectors";
import { useGame } from "../state/store";

export function DawnScreen({ view }: { view: PlayerView }) {
  const roles = useGame((s) => s.roles);
  const setUi = useGame((s) => s.setUi);
  const deaths = view.day?.deaths_last_night ?? [];
  return (
    <main className="screen">
      <PhaseHeader view={view} roles={roles} />
      <div className="grow stack center" style={{ justifyContent: "center" }}>
        <div className="sun" aria-hidden />
        <span className="eyebrow">Dawn</span>
        <h1>Day {view.day_number}</h1>
        <p style={{ fontSize: "1.25rem" }}>{dawnHeadline(view)}</p>
        {(view.day?.revived_last_night.length ?? 0) > 0 && (
          <p style={{ fontSize: "1.25rem" }}>{revivedHeadline(view)}</p>
        )}
        {deaths.length > 0 && (
          <p className="small muted">The dead may still talk, and each has one vote left.</p>
        )}
      </div>
      <div className="bottom-bar">
        <button
          className="btn btn-primary"
          onClick={() => setUi({ dawnSeenDay: view.day_number })}
        >
          To the town square
        </button>
      </div>
    </main>
  );
}

export function DayScreen({ view }: { view: PlayerView }) {
  const roles = useGame((s) => s.roles);
  const sheetSeatId = useGame((s) => s.ui.sheetSeatId);
  const setUi = useGame((s) => s.setUi);
  const { run, busy } = useCommand();
  const [stating, setStating] = useState(false);
  const day = view.day!;
  const priv = view.private;
  const me = view.seats.find((s) => s.seat_id === view.me_seat_id);
  // statements get a card of their own, so the log never pushes one out of sight
  const todaysLog = view.log.filter(
    (e) => e.day_number === view.day_number && e.type !== "dawn" && e.type !== "statement",
  );

  return (
    <main className="screen">
      <PhaseHeader view={view} roles={roles} />

      <div className="row-between small">
        <span className="muted">
          {day.living_count} alive · {day.threshold} votes to execute
        </span>
        {day.day_deadline_ms && (
          <Countdown deadline={day.day_deadline_ms} prefix="Day ends in " className="muted" />
        )}
      </div>

      <TownSquare
        view={view}
        blockSeatId={day.block?.seat_id ?? null}
        showReady
        onSeatTap={(seatId) => setUi({ sheetSeatId: seatId })}
      >
        {day.block ? (
          <>
            <span className="eyebrow">On the block</span>
            <strong>{seatName(view, day.block.seat_id)}</strong>
            <span className="small muted">{day.block.votes} votes</span>
          </>
        ) : (
          <span className="small muted">Nobody on the block</span>
        )}
      </TownSquare>

      {priv.can_death_choice && <DeathChoiceCard required={priv.death_choice_required} />}

      <NominationHint view={view} />

      {priv.can_make_statement && (
        <button className="btn btn-ghost" onClick={() => setStating(true)}>
          Make a statement…
        </button>
      )}

      {day.statements.length > 0 && (
        <section className="card card-tight stack-sm">
          <span className="eyebrow">Said today</span>
          <ul className="log">
            {day.statements.map((st) => (
              <li key={st.seat_id} className="small">
                <strong>{seatName(view, st.seat_id)}:</strong> “{st.text}”
              </li>
            ))}
          </ul>
        </section>
      )}

      {todaysLog.length > 0 && (
        <section className="card card-tight stack-sm">
          <span className="eyebrow">Today</span>
          <ul className="log">
            {todaysLog.slice(-6).map((e) => (
              <li key={`${e.seq}-${e.text}`} className="small">
                {e.text}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="grow" />
      <div className="bottom-bar">
        {priv.can_ready_for_night ? (
          <button
            className={`btn ${priv.ready_for_night ? "btn-ghost" : ""}`}
            disabled={busy}
            onClick={() => run({ type: "ready_for_night", ready: !priv.ready_for_night })}
          >
            {priv.ready_for_night ? "Not ready yet" : "Ready for night"}
            <span className="chip tiny">
              {day.ready_count}/{day.ready_needed}
            </span>
          </button>
        ) : (
          me &&
          !me.alive && (
            <p className="small muted center">
              You are dead. Keep talking: you can still vote once with your ghost vote.
            </p>
          )
        )}
      </div>

      <SeatSheet
        view={view}
        seat={view.seats.find((s) => s.seat_id === sheetSeatId) ?? null}
        onClose={() => setUi({ sheetSeatId: null })}
      />
      {stating && (
        <StatementSheet open onClose={() => setStating(false)} view={view} roles={roles} />
      )}
    </main>
  );
}

/** A player who has just died may publicly choose a living player (the Moonchild's ability).
 * Everyone who dies is offered it, so being offered it says nothing. The Moonchild's own phone
 * (`required`) offers no "no one": they must choose. */
export function DeathChoiceCard({ required }: { required: boolean }) {
  const { run, busy } = useCommand();
  return (
    <section className="card stack-sm" role="status">
      <span className="eyebrow">You died</span>
      {required ? (
        <>
          <p>
            You are the <strong>Moonchild</strong>, so you must choose a living player. Say out
            loud who you choose, then tap them and choose them. If they are good, they die
            tonight.
          </p>
          <p className="small muted">
            If you have not chosen when night falls, the app chooses a living player for you at
            random, and every phone shows it.
          </p>
        </>
      ) : (
        <>
          <p>
            If you are the <strong>Moonchild</strong>, say out loud which living player you
            choose, then tap them and choose them. If they are good, they die tonight.
          </p>
          <p className="small muted">
            Anyone who has just died may choose. Only the Moonchild&apos;s choice does anything.
          </p>
          <button
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => run({ type: "death_choice", target_seat_id: null })}
          >
            Choose no one
          </button>
        </>
      )}
    </section>
  );
}

function NominationHint({ view }: { view: PlayerView }) {
  const day = view.day!;
  const me = view.seats.find((s) => s.seat_id === view.me_seat_id);
  if (!day.nominations_open) {
    return (
      <p className="small muted center">
        Talk it over. Nominations open in{" "}
        <Countdown deadline={day.nominations_open_at_ms} className="mono" />
      </p>
    );
  }
  if (view.private.can_nominate) {
    return (
      <p className="small center">
        Tap a player to nominate them for execution. You may nominate yourself, or a dead player.
      </p>
    );
  }
  if (me && !me.alive) return <p className="small muted center">The dead cannot nominate.</p>;
  if (me?.has_nominated_today) {
    return <p className="small muted center">You have nominated today.</p>;
  }
  return <p className="small muted center">Nobody left for you to nominate today.</p>;
}

function SeatSheet({
  view,
  seat,
  onClose,
}: {
  view: PlayerView;
  seat: SeatPublic | null;
  onClose: () => void;
}) {
  const { run, busy } = useCommand();
  const [confirmShot, setConfirmShot] = useState(false);
  if (!seat) return null;
  const isMe = seat.seat_id === view.me_seat_id;
  const priv = view.private;
  // the server says who may be nominated (yourself and the dead too) and who may shoot; a shot
  // may be at anyone, alive or dead, yourself included
  const canNominateThem = priv.nominee_seat_ids.includes(seat.seat_id);
  const canShootThem = priv.can_shoot;
  const canChooseThem = priv.can_death_choice && seat.alive && !isMe;
  const who = isMe ? "yourself" : seat.name;
  const close = () => {
    setConfirmShot(false);
    onClose();
  };

  const nominate = async () => {
    const r = await run({ type: "nominate", nominee_seat_id: seat.seat_id });
    if (r.kind === "ack") close();
  };
  const shoot = async () => {
    const r = await run({ type: "slayer_shoot", target_seat_id: seat.seat_id });
    if (r.kind === "ack") close();
  };
  const choose = async () => {
    const r = await run({ type: "death_choice", target_seat_id: seat.seat_id });
    if (r.kind === "ack") close();
  };

  return (
    <Sheet open onClose={close} label={seat.name}>
      <div className="stack-sm">
        <h3>
          {seat.name}
          {isMe && <span className="muted"> (you)</span>}
        </h3>
        <p className="small muted">
          {seat.alive ? "Alive" : seat.ghost_vote_available ? "Dead · ghost vote left" : "Dead"}
          {seat.nominated_today && " · already nominated today"}
          {seat.has_nominated_today && " · has nominated today"}
          {!seat.connected && " · offline"}
        </p>
      </div>
      {confirmShot ? (
        <div className="stack">
          <p>
            Shoot {who}? If you are the Slayer and they are the Demon, they die. Otherwise nothing
            happens. Anyone may try, but everyone gets only one shot per game.
          </p>
          <button className="btn btn-danger" disabled={busy} onClick={shoot}>
            Shoot {who}
          </button>
          <button className="btn btn-ghost" onClick={() => setConfirmShot(false)}>
            Back
          </button>
        </div>
      ) : (
        <div className="stack">
          {canNominateThem && (
            <button className="btn btn-primary" disabled={busy} onClick={nominate}>
              Nominate {who}
            </button>
          )}
          {canShootThem && (
            <button className="btn" onClick={() => setConfirmShot(true)}>
              Slayer shot…
            </button>
          )}
          {canChooseThem && (
            <button className="btn" disabled={busy} onClick={choose}>
              Choose {seat.name} (Moonchild)
            </button>
          )}
          {!canNominateThem && !canShootThem && !canChooseThem && (
            <p className="small muted">Nothing to do here right now.</p>
          )}
          <button className="btn btn-ghost" onClick={close}>
            Close
          </button>
        </div>
      )}
    </Sheet>
  );
}
